import { useAuth } from '@bapecom/auth';
import { User, Mail, Shield, Building, Key } from 'lucide-react';

export const Profile: React.FC = () => {
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12 bg-white dark:bg-slate-800/20 backdrop-blur-md border border-slate-200 dark:border-slate-700/50 rounded-2xl shadow-sm">
          <p className="text-slate-600 dark:text-slate-400">User information not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in duration-500">
      <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-8">My Profile</h1>

      {/* Profile Card */}
      <div className="bg-white dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-6 shadow-sm">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold border-2 border-slate-200 dark:border-slate-700">
            {user.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{user.name}</h3>
            <p className="text-slate-600 dark:text-slate-400">{user.email}</p>
            <div className="mt-2 flex items-center space-x-2">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${
                user.role === 'admin' 
                  ? 'bg-red-500/10 dark:text-red-400 text-red-600 border-red-500/20' 
                  : user.role === 'manager'
                    ? 'bg-purple-500/10 dark:text-purple-400 text-purple-600 border-purple-500/20'
                    : 'bg-slate-200 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-500/20'
              }`}>
                {user.role}
              </span>
              {user.department && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium border bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-500/20">
                  {user.department}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-white dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-6 shadow-sm">
        <h4 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <User size={18} className="text-indigo-500 dark:text-indigo-400" />
          Details
        </h4>
        <dl className="grid grid-cols-1 gap-4">
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <Key size={16} className="text-slate-400 dark:text-slate-500" />
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-500">ID</dt>
              <dd className="text-slate-900 dark:text-slate-200 font-mono text-sm">{user.id}</dd>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <Mail size={16} className="text-slate-400 dark:text-slate-500" />
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-500">Email</dt>
              <dd className="text-slate-900 dark:text-slate-200">{user.email}</dd>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <Shield size={16} className="text-slate-400 dark:text-slate-500" />
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-500">Role</dt>
              <dd className="text-slate-900 dark:text-slate-200 capitalize">{user.role}</dd>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <Building size={16} className="text-slate-400 dark:text-slate-500" />
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-500">Department</dt>
              <dd className="text-slate-900 dark:text-slate-200">{user.department || 'Not assigned'}</dd>
            </div>
          </div>
        </dl>
      </div>

      {/* Apps Access Card */}
      <div className="bg-white dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
        <h4 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Shield size={18} className="text-emerald-500 dark:text-emerald-400" />
          App Access
        </h4>
        {user.apps && user.apps.length > 0 ? (
          <div className="space-y-2">
            {user.apps.map((appId) => (
              <div
                key={appId}
                className="flex items-center justify-between py-3 px-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
              >
                <span className="text-slate-900 dark:text-slate-200 capitalize">{appId.replace(/-/g, ' ')}</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 dark:text-emerald-400 text-emerald-600 border border-emerald-500/20">
                  Granted
                </span>
              </div>
            ))}
          </div>
        ) : user.role === 'admin' ? (
          <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
            <p className="text-indigo-600 dark:text-indigo-300 text-sm">
              🔐 Admin has access to all apps
            </p>
          </div>
        ) : (
          <p className="text-slate-500 dark:text-slate-500">No app access granted yet</p>
        )}
      </div>
    </div>
  );
};
